import { useState, useEffect, useCallback, useRef } from 'react';

interface PaginatedResponse<T> {
  items: T[];
  continuationToken?: string;
}

interface UsePaginatedApiOptions {
  enabled?: boolean;
  /**
   * Largest number of pages to request in one load. Omit to keep following continuation tokens
   * until the collection is exhausted, which is only appropriate for collections that are bounded
   * in size (rules, guidance entries, versions of one rule).
   *
   * With a bound set, the hook stops on the page that reaches it and reports `hasMore`; the caller
   * asks for the next batch of the same size with `loadMore()`.
   */
  maxPages?: number;
}

interface UsePaginatedApiResult<T> {
  data: T[];
  loading: boolean;
  fetchingMore: boolean;
  error: string | null;
  /** A continuation token is being held: more items exist beyond `data`. Always false when unbounded. */
  hasMore: boolean;
  /** Request the next batch of up to `maxPages` pages. No-op while a load is running or when exhausted. */
  loadMore: () => void;
  refetch: () => void;
}

export function usePaginatedApi<TParams, TItem>(
  fetchFn: (params: TParams & { continuationToken?: string }) => Promise<PaginatedResponse<TItem>>,
  params: TParams,
  options?: UsePaginatedApiOptions,
): UsePaginatedApiResult<TItem> {
  const [data, setData] = useState<TItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const enabled = options?.enabled ?? true;
  const maxPages = options?.maxPages;

  // Identifies the current load. A response whose generation is stale is discarded. A boolean
  // cancelled flag cannot express this: the next effect run resets it to false, so a request still
  // in flight from the previous run sees false again and appends its items to the new load.
  const generationRef = useRef(0);
  // Accumulated items and the held continuation token, mirrored in refs so `loadMore` can resume
  // without being rebuilt (and re-running the effect) on every state change.
  const dataRef = useRef<TItem[]>([]);
  const tokenRef = useRef<string | undefined>(undefined);
  const runningRef = useRef(false);
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  const runPages = useCallback(
    async (generation: number, startToken: string | undefined) => {
      runningRef.current = true;
      let token = startToken;
      let pages = 0;

      try {
        for (;;) {
          const fetchParams = {
            ...paramsRef.current,
            ...(token ? { continuationToken: token } : {}),
          } as TParams & { continuationToken?: string };
          const response = await fetchFnRef.current(fetchParams);
          if (generationRef.current !== generation) return;

          dataRef.current = [...dataRef.current, ...response.items];
          setData(dataRef.current);
          token = response.continuationToken;
          tokenRef.current = token;
          pages += 1;

          const willContinue = !!token && (maxPages === undefined || pages < maxPages);
          setHasMore(!!token && !willContinue);
          if (!willContinue) return;

          // Release the blocking spinner once the first page is on screen; the rest of the batch
          // arrives under the lighter `fetchingMore` indicator.
          if (pages === 1) {
            setLoading(false);
            setFetchingMore(true);
          }
        }
      } catch (err) {
        if (generationRef.current !== generation) return;
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
        // A held token survives the failure, so a retry resumes rather than restarting.
        setHasMore(!!tokenRef.current);
      } finally {
        if (generationRef.current === generation) {
          runningRef.current = false;
          setLoading(false);
          setFetchingMore(false);
        }
      }
    },
    [maxPages],
  );

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    dataRef.current = [];
    tokenRef.current = undefined;
    setData([]);
    setHasMore(false);
    setError(null);
    setFetchingMore(false);

    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    runPages(generation, undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), refetchTrigger, enabled, runPages]);

  const loadMore = useCallback(() => {
    if (runningRef.current || !tokenRef.current) return;
    setError(null);
    setFetchingMore(true);
    runPages(generationRef.current, tokenRef.current);
  }, [runPages]);

  const refetch = useCallback(() => setRefetchTrigger((prev) => prev + 1), []);

  return { data, loading, fetchingMore, error, hasMore, loadMore, refetch };
}
